import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import React, { useMemo, useRef } from 'react';
import { TKeywordUrlPreviewFilterProps } from '../../../types';
import moment, { Moment } from 'moment';
import {
  FilterKeys,
  KeyOperateType,
  TFilterGroupKeys,
} from '@op/components/FilterCommon/types';
import { asyncCreateShortUrl } from '@op/components/FilterCommon/utils/historyUtils';
import { useLocation, useParams } from 'umi';
import useAdvancedSummary from '@op/components/GPT/hooks/useAdvancedSummary';
import {
  keywordDrillUrlPreviewsDisabledTips,
  TipsMapType,
} from '@op/components/BubbleCommon';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { useGetShortId } from '@op/hooks/useShortId';
import { channelSplitFlag } from '@op/components/FilterCommon/constants/channel';
import { useInjectionContext } from '../../../provider';
import {
  useCreateKeywordAnalysis,
  useStopTopicAnalysis,
} from '../../../hook/useTopicAnalysisFlow';
import { notification, Modal } from 'tea-component';
import { updateURL } from '@op/pages/AIAnalysisHub/hooks';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';

const resetTips: TipsMapType = {
  zh: '重置为报告初始筛选条件及AI报告',
  en: 'Reset to original filters and AI report',
};

const resetDisabledTips: TipsMapType = {
  zh: '当前已是初始报告',
  en: 'This is already the original report',
};

const tab = 'keyword';
export default function KeywordUrlPreviewFilter({
  gameInfo,
  changeFilterValue,
  shortId: pramsShortId,
  setLoading,
  wholeGameInfo,
  promptList,
  videoPromptList,
  loading,
  isCreator,
  resetAnalysis,
  setTranslateTopicMap,
}: TKeywordUrlPreviewFilterProps) {
  const {
    taskId,
    dataType,
    isTopicAnalysis,
    versionId,
    originalShortUrl,
    isSubscription,
    replaceParams = {},
    successTagged,
    title,
    hideFilterOperateBtn,
    allPreview,
  } = useInjectionContext();

  const localtion = useLocation();
  const { id } = useParams<{ id: string }>();
  const { formatMessage } = useLanguage();
  const isFirst = useRef(true);
  const {
    remainder,
    total,
    loading: advancedLimitLoading,
  } = useAdvancedSummary({ defaultLoading: true });

  function handleErrorConfirm() {
    changeFilterValue({
      filter: null,
      oriFilter: null,
      pageFilter: null,
      tab,
      taskId,
      versionId,
    });
    setLoading(false);
  }

  const dynamicsConfig = useMemo(() => {
    return {
      remainder,
      total,
    };
  }, [remainder, total]);

  const { scrollBody, filterOptions, confirmRef, filterConfirm } =
    useFilterDataHook({
      gameInfo,
      pageName: 'Summary_Keywords_Drill',
    });
  const { updateAnalysis: stopAnalysis, loadingRef: stopLoading } =
    useStopTopicAnalysis();

  async function handleStopAnalysis() {
    const yes = await Modal.confirm({
      message: formatMessage({ id: 'Stop Analysis?' }),
      description: formatMessage(
        {
          id: 'Stop {name}? This action will delete the analysis and is irreversible. Please proceed with caution.',
        },
        { name: <b>{title}</b> },
      ),
      okText: formatMessage({ id: 'Confirm' }),
      cancelText: formatMessage({ id: 'Cancel' }),
    });

    if (yes && taskId && !stopLoading.current) {
      await stopAnalysis({ task_id: taskId, ...gameInfo });
      handleErrorConfirm();
    }
  }

  const { createAnalysisTask } = useCreateKeywordAnalysis({ isCreator });

  const filterOptionsList: Array<FilterKeys> = useMemo(() => {
    if (isTopicAnalysis) {
      return [
        ...filterOptions,
        'gptType',
        'topicInputPreview',
        'gptTriggerAgent',
      ];
    }
    return [...filterOptions, 'keywrodInputPreview', 'gptPreview'];
  }, [filterOptions]);

  async function keywordConfirm(filter: any, saveFilter: any) {
    const { promptInfo, ...otherFilter } = filter;

    let shortId = pramsShortId;
    let data: any = { task_id: taskId, version_id: versionId };
    if (!isFirst.current && isCreator) {
      try {
        shortId = await asyncCreateShortUrl({
          filter: saveFilter,
          gameInfo,
          updateUrl: false,
          errorText: formatMessage({
            id: 'Analysis Generate failed, please try again',
          }),
        });
        if (!shortId) return handleErrorConfirm();
        const promptDetail = promptInfo.keyword || {};
        const taskParams: any = {
          short_url: shortId,
          data_type: dataType,
          need_summary: false,
          ...otherFilter,
          task_id: taskId,
          model_type: promptDetail?.modelType,
          prompt: promptDetail?.prompt || '',
          keyword_analysis_task_type: 'temporary',
          keyword_analysis_task_category: isTopicAnalysis ? 'topic' : 'normal',
        };
        console.log(taskParams, 'taskParams');
        data = await createAnalysisTask(taskParams);

        if (dataType === 'new') {
          // 将创建出来的versionId设置到url上
          updateURL({
            updateParams: { version_id: data.version_id },
          });
          if (!data?.version_id) {
            notification.error({
              description: formatMessage({
                id: 'Analysis Generate failed, please try again',
              }),
            });
            return handleErrorConfirm();
          }
        }
      } catch (error: any) {
        return handleErrorConfirm();
      }
    }

    // 更新过滤器内容
    changeFilterValue({
      filter: otherFilter,
      oriFilter: saveFilter,
      tab,
      taskId: data.task_id,
      versionId: data.version_id,
      pageFilter: {
        id,
        short_url: shortId,
        category: isTopicAnalysis ? 'topic' : 'keyword',
        ...gameInfo,
      },
      promptInfo,
      isFirst: isFirst.current,
    });
    isFirst.current = false;
  }

  function dealwithOperationLog(data: any) {
    const { promptInfo, ...filter } = data || {};
    const keys = Object.keys(promptInfo || {});

    const dataInfo = keys.reduce((pre, cur) => {
      const { prompt, ...info } = promptInfo[cur];
      return { ...pre, [cur]: info };
    }, {});

    return {
      ...filter,
      promptInfo: dataInfo || {},
    };
  }

  async function resetKeywordConfirm(
    filter: any,
    saveFilter: any,
    report?: any,
  ) {
    if (!report) return;
    const { promptInfo, ...otherFilter } = filter;
    try {
      // 更新过滤器内容
      changeFilterValue({
        filter: otherFilter,
        oriFilter: saveFilter,
        tab,
        pageFilter: {
          short_url: report.short_url,
          category: tab,
          ...gameInfo,
        },
      });
    } catch (error) {
      handleErrorConfirm();
    }
  }

  const reportData = useRef(null);
  async function confirm(filterData: any, oriFilterValue: any) {
    setLoading(true);
    const filter = { ...filterData, ...gameInfo };
    const saveFilter = { ...oriFilterValue, ...gameInfo };
    const confirmFunc: (
      filterData: any,
      oriFilterValue: any,
      reportData?: any,
    ) => Promise<void> = reportData.current
      ? resetKeywordConfirm
      : keywordConfirm;

    try {
      await confirmFunc(filter, saveFilter, reportData.current);
      const operationFilter = dealwithOperationLog(filter);

      buriedBtnPoint(
        'GenerateReport',
        operationFilter,
        saveFilter?.keywordCustomInput,
        `${saveFilter?.content_urls}`,
      );
    } catch (error) {}
    reportData.current = null;
  }

  const { filter, loading: shortIdLoading } = useGetShortId({
    shortid: originalShortUrl,
    needRequest: isSubscription || isTopicAnalysis,
    defaultLoading: isSubscription || isTopicAnalysis,
    defaultValue: isSubscription ? replaceParams : {},
  });
  function getDisableTimeFunc(type: 'M' | 'w' | 'd' | 'h') {
    return (date: Moment) => {
      const { start_time, end_time } = filter || {};
      if (!start_time || !end_time) {
        return true;
      }
      const startTime = moment(start_time);
      const endTime = moment(end_time);

      return (
        (date.isSame(startTime, type) || date.isAfter(startTime, type)) &&
        (date.isSame(endTime, type) || date.isBefore(endTime, type))
      );
    };
  }

  function getDefaultConfig() {
    if (isTopicAnalysis) {
      return {
        date: {
          disabledDate: {
            date: getDisableTimeFunc('d'),
            hour: getDisableTimeFunc('h'),
            month: getDisableTimeFunc('M'),
            week: getDisableTimeFunc('w'),
          },
          disabledTips: `This date hasn't been tagged with AI Topics yet. Please pick one from the available tagged dates.`,
        },
        reviewType: {
          hasAllSelected: (filter?.review_types || [])?.length === 0,
          dealwithOptions: (options: any[], key: string) => {
            const selectedValue = filter?.[key as string] || [];
            if (selectedValue.length === 0) {
              return options;
            }
            return options.filter((item) => selectedValue.includes(item.value));
          },
        },
        sentiment: {
          hasAllSelected: (filter?.sentiment || [])?.length === 0,
          dealwithOptions: (options: any[], key: string) => {
            const selectedValue = filter?.[key as string] || [];

            if (selectedValue.length === 0) {
              return options;
            }

            if (selectedValue[0] === '-99999') {
              return [];
            }

            return options.filter((item) =>
              selectedValue.includes(Number(item.value)),
            );
          },
        },
        language: {
          showShortcut: false,
          dealwithOptions: (options: any[], key: string) => {
            const selectedValue = filter?.[key as string] || [];

            if (selectedValue.length === 0) {
              return options;
            }

            if (selectedValue[0] === '-99999') {
              return [];
            }

            return options
              .map((item) => {
                const list = item.children?.filter((it) =>
                  selectedValue.includes(it.value),
                );
                if (list.length === 0) return null;

                return {
                  ...item,
                  children: list,
                };
              })
              .filter(Boolean);
          },
        },
        channel: {
          dealwithOptions: (options: any[], keys: Record<string, string>) => {
            const keyMap = keys as Record<string, string>;
            return options
              .map((item) => {
                const selectedValue =
                  filter?.[keyMap[item.value] as string] || [];
                if (selectedValue.length === 0) return item;

                if (selectedValue[0] === '-99999') return null;
                const list = item.children?.filter((it) =>
                  selectedValue.includes(
                    it?.value?.split(channelSplitFlag)?.[1],
                  ),
                );

                if (list.length === 0) return null;

                return {
                  ...item,
                  children: list,
                };
              })
              .filter(Boolean);
          },
        },
        topic: {
          disabledTips: {
            zh: '生成报告时，该数据已被排除',
            en: 'This data has been excluded when generating the report',
          },
          showShortcut: false,
          hasAllOptions: (filter?.topic || [])?.length === 0,
          disabledOptions: (filter?.topic_rule || '').includes('include')
            ? []
            : filter?.topic || [],
          dealwithOptions: (options: any[], key: string) => {
            const selectedValue = filter?.[key as string] || [];
            if (selectedValue.length === 0) {
              return options;
            }

            if (selectedValue[0] === '-99999') {
              return [];
            }

            const topicRule = filter?.topic_rule || 'include';
            const isInclude = topicRule.includes('include');

            return options
              .map((item) => {
                const list = item.children?.filter((it: any) => {
                  const flag = selectedValue.includes(it.value);
                  if (!isInclude) {
                    return true;
                  }
                  return flag;
                });
                if (list.length === 0) return null;

                return {
                  ...item,
                  children: list,
                };
              })
              .filter(Boolean);
          },
        },
      };
    }

    return {};
  }

  function getNoPreviewGroups(): TFilterGroupKeys[] {
    if (hideFilterOperateBtn) {
      return [];
    }
    if (isTopicAnalysis) {
      return ['popular', 'gpt', 'keyword', 'gptAnalysisTarget'];
    }
    return [];
  }

  function getFilterBtnList(): KeyOperateType[] {
    // 只读内嵌场景：隐藏全部操作按钮（确定 / 重置 / Stop）
    if (hideFilterOperateBtn) {
      return [];
    }
    if (isCreator && dataType === 'new') {
      // 仅针对 Keyword Topic 分析（isTopicAnalysis = true）且 loading 状态
      if (isTopicAnalysis && loading) {
        // 例行报告不显示 Stop 按钮
        if (isSubscription) {
          return ['confirm'];
        }
        console.log('当前分析未完成，显示 Stop 按钮', successTagged);
        // 一次性报告：仅在标注未完成时显示 Stop 按钮
        return successTagged ? ['confirm'] : ['stop', 'reset'];
      }
      return isSubscription ? ['confirm'] : ['confirm', 'reset'];
    }
    return ['confirm'];
  }
  // console.log(pramsShortId, 'pramsShortId');
  return (
    <div className="keyword-url-filter-wrapper">
      {!advancedLimitLoading && !shortIdLoading && (
        <FilterFixed
          filter={filterOptionsList}
          onlySaveFilters={['keyWords']}
          onStop={() => {
            handleStopAnalysis();
          }}
          confirmRef={confirmRef}
          operateShowOptions={['more']}
          isPreviews={
            (dataType === 'new' && isTopicAnalysis) || hideFilterOperateBtn
          }
          allPreview={allPreview}
          noPreviewsGroups={getNoPreviewGroups()}
          keyOperateShowOptions={getFilterBtnList()}
          scrollBody={scrollBody}
          shortid={pramsShortId}
          beforeCacheDealwithData={() => false}
          defaultConfig={
            {
              gptPreview: {
                wholeGameInfo,
                tab,
                promptList,
                videoPromptList,
              },
              ...getDefaultConfig(),
            } as any
          }
          finallyDealwithDefaultValue={(
            localFilter,
            currentFilter,
            isDefaultValue,
          ) => {
            const { start_time, end_time } = filter || {};
            const data = isDefaultValue ? currentFilter : localFilter;
            if (start_time && end_time) {
              return { ...data, start_time, end_time };
            }
            return data;
          }}
          finallyDealwithResetValue={async () => {
            await resetAnalysis?.();
          }}
          disabledTips={
            loading ? keywordDrillUrlPreviewsDisabledTips : undefined
          }
          dynamicsConfig={dynamicsConfig}
          confirm={(value: any, oriFilterValue: any) => {
            confirm(value, oriFilterValue);
          }}
          resetBtnText="Revert to original report"
          resetBtnTips={!versionId ? resetDisabledTips : resetTips}
          resetDisabled={!versionId}
          backCondOriginData={(data) => {
            setTranslateTopicMap?.(data?.topic_trans || []);
          }}
          confirmLoading={loading}
        ></FilterFixed>
      )}
    </div>
  );
}
